import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import AnimatedVisual from "@/components/AnimatedVisual";

export default function Hero({ onOpenDemo }) {
  return (
    <section className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32" data-testid="home-hero">
      <div className="absolute inset-0 hero-glow" aria-hidden />
      <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
      <div className="absolute -bottom-1 left-0 right-0 h-40 bg-gradient-to-t from-[#050816] to-transparent" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-gray-300">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00D4FF] shadow-[0_0_10px_#00D4FF]" />
              B2B iGaming Technology Provider · Since 2015
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[1.02]"
            data-testid="hero-title"
          >
            Powering the <span className="text-gradient-blue">Next Generation</span><br/>
            of <span className="text-gradient">iGaming</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base lg:text-lg text-gray-400 leading-relaxed max-w-2xl"
          >
            Turnkey & white-label casino, sportsbook and crypto casino platforms — engineered
            for licensed operators. Launch under your brand in as little as 72 hours with 10,000+ games,
            250+ payment integrations and enterprise-grade infrastructure.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Button
              data-testid="hero-book-demo"
              onClick={onOpenDemo}
              className="btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-12 px-7 text-white"
            >
              Book a Demo <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              data-testid="hero-explore-solutions"
              className="rounded-full h-12 px-7 bg-white/5 hover:bg-white/10 text-white border border-white/10"
              onClick={() => document.getElementById("solutions")?.scrollIntoView({behavior:"smooth"})}
            >
              Explore Solutions
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.6 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg"
          >
            {[
              { n: "72h", l: "Live launch" },
              { n: "10K+", l: "Games" },
              { n: "250+", l: "Payments" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-3xl font-bold text-white">{s.n}</div>
                <div className="text-xs text-gray-500 mt-1 tracking-wide uppercase">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative aspect-square rounded-3xl overflow-hidden glass-strong"
          >
            <AnimatedVisual variant="hero" className="absolute inset-0" label="iGaming platform animation"/>
            <div className="absolute inset-0 bg-gradient-to-tr from-[#050816] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 p-5 rounded-2xl glass-strong">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Certified & Licensed</div>
                  <div className="text-xs text-gray-400">Curaçao · Malta · Anjouan · MGA</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="hidden lg:flex absolute -left-8 top-8 items-center gap-2 px-4 py-2.5 rounded-full glass-strong"
          >
            <Sparkles className="w-4 h-4 text-[#00D4FF]" />
            <span className="text-xs">450+ Active Projects Worldwide</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
